import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { MONTHS_EL } from "@/lib/format";
import { ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";
import { OccCell } from "@/components/OccCell";
import { PlanGrid } from "@/components/PlanGrid";

const pad = (n) => String(n).padStart(2, "0");

export default function VillaPlan() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const last = new Date(ym.y, ym.m, 0).getDate();
  const from = `${ym.y}-${pad(ym.m)}-01`;
  const to = `${ym.y}-${pad(ym.m)}-${pad(last)}`;
  const { data, isLoading } = useQuery({ queryKey: ["pms-plan", from, to], queryFn: async () => (await api.get(`/pms/plan?date_from=${from}&date_to=${to}`)).data });
  const shift = (k) => setYm(({ y, m }) => { const t = m + k; return t < 1 ? { y: y - 1, m: 12 } : t > 12 ? { y: y + 1, m: 1 } : { y, m: t }; });
  const days = data?.days || [];
  const avg = days.length ? Math.round(days.reduce((s, d) => s + d.pct, 0) / days.length) : 0;
  const nights = days.reduce((s, d) => s + d.booked, 0);
  const btn = "p-2 rounded-xl border border-[#E3DED4] bg-white hover:bg-[#F7F5F1] transition-colors";

  return (
    <div className="space-y-6" data-testid="villa-plan-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Κρατήσεις</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#45423C] mt-1 flex items-center gap-3"><CalendarRange className="h-8 w-8" /> Πλάνο Βιλών</h1>
          <p className="text-sm text-[#6F6A60] mt-2">Πληρότητα ανά βίλα και ημέρα από τις κρατήσεις Hotelizer (τελευταίος συγχρονισμός).</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="plan-prev" className={btn} onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></button>
          <div className="min-w-[150px] text-center font-medium text-[#45423C]" data-testid="plan-month">{MONTHS_EL[ym.m]} {ym.y}</div>
          <button data-testid="plan-next" className={btn} onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></button>
          <button data-testid="plan-today" className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold text-[#45423C] hover:bg-[#F7F5F1]" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() + 1 })}>Σήμερα</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Μέση πληρότητα", `${avg}%`, "plan-kpi-avg"], ["Νύχτες κλεισμένες", `${nights} / ${days.length * (data?.villas.length || 0)}`, "plan-kpi-nights"],
          ["Αφίξεις μήνα", days.reduce((s, d) => s + d.arrivals, 0), "plan-kpi-arr"], ["Βίλες", data?.villas.length ?? "—", "plan-kpi-villas"]].map(([l, v, t]) => (
          <div key={t} className="rounded-2xl bg-white border border-[#E3DED4] p-4" data-testid={t}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className="font-serif text-2xl text-[#45423C] mt-1 tabular">{v}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        {isLoading ? <p className="p-8 text-center text-[#6F6A60]">Φόρτωση…</p> : <PlanGrid data={data} OccCell={OccCell} />}
      </div>
      <p className="text-xs text-[#6F6A60]">Χρώματα πληρότητας: <span className="px-1.5 rounded bg-[#F1EDE6]">&lt;50%</span> <span className="px-1.5 rounded bg-[#E9C9A8]">50–84%</span> <span className="px-1.5 rounded bg-[#C86235] text-white">≥85%</span> · ↓ αφίξεις ↑ αναχωρήσεις</p>
    </div>
  );
}
